import React, { CSSProperties, PureComponent } from 'react';
import {
    Airspace,
    AirspaceList,
    AirspaceListRequest,
    AirspaceRef,
    FlightDto,
    MapFlightsDataDto,
    RegulationOrMcdmOnlyDto,
    RegulationState,
    TimePeriod,
    TrafficType,
} from 'app/model/CORE';
import {
    MonitoredMeasure,
    RadRestriction,
    SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
    Sol49MapFlightsDataRequestDto,
} from 'app/model/SOL49';
import AppContext from '../../../../common/context/AppContext';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { FlightMapOptions } from '../../../map/model/MapModel';
import { Flex } from '../../../common/Flex';
import FlightLevelUtils from '../../../../common/util/FlightLevelUtils';
import MapUtils, { MonitorableMapMeasure } from '../../../map/util/MapUtils';
import TimeUtils from '../../../../common/util/TimeUtils';
import { TimeSelector } from '../../../map/control/TimeSelector';
import * as style from 'ol/style';
import OLMapStyles from '../../../map/ol/OLMapStyles';
import { setLoadConvection } from 'app/redux/features/dashboard/dashboard-slice';
import { RootState } from 'app/redux/store';
import { connect, ConnectedProps } from 'react-redux';
import {
    MapTrafficVolumeCacheType,
    MapTrafficVolumeCacheUpdated,
} from 'app/components/dashboard/network/map/MapTrafficVolumeCache';
import MeasureUtils from 'app/common/util/MeasureUtils';
import {
    EnvironmentLayerUtils,
    MapLayer,
    MapLayerType,
    MapLayerUtils,
    MapObjectType,
    ObjectToDisplay,
    RegulatedLayerDelayLevelEnum,
} from 'app/components/dashboard/network/map/Map';
import LayerSelectionEntry from 'app/components/dashboard/network/map/LayerSelectionEntry';
import { LayerUpdateEvent, ShowMapLayerEvent } from 'app/common/eventbus/Events';
import difference from 'lodash/difference';
import memoizeOne from 'memoize-one';
import { LayerSelectionEntryShortcut } from 'app/components/dashboard/network/map/LayerSelectionEntryShortcut';
import { notification } from 'antd';
import { UnityApplication } from 'app/unity/UnityApplication';
import { NMUI4DContext, NMUI4DContextProps } from 'app/common/context/NMUI4DContext';

const mapDispatchToProps = {
    setLoadConvection,
};
const mapStateToProps = (state: RootState) => {
    return {
        mapConvectionRedux: state.dashboardData.convectionData,
        monitoredMeasures: state.dashboardData.dashboardData.monitoredMeasures,
        tvCache: state.mapFullscreenData.tvCache,
    };
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface RadRestrictionMapPanelProps extends PropsFromRedux {
    selectedTimePeriod: TimePeriod;
    selectedRadRestriction: RadRestriction;
    selectedFlights?: Array<FlightDto>;
}
interface RadRestrictionMapPanelState {
    airspaceData?: AirspaceList;
    mapFlightsData?: MapFlightsDataDto;
    refTime: Date;
    displayConvection: boolean;
    displayOffload: boolean;
    displayOnload: boolean;
    displayTv: boolean;
    displayAltRoutes: boolean;
    measureLayer: MapLayer;
}
class RadMapPanel extends PureComponent<RadRestrictionMapPanelProps, RadRestrictionMapPanelState> {
    private mapId: number;
    private static mapOptions: FlightMapOptions = {
        autoZoom: false,
        listVectorPoints: true,
        showActiveAirspaceOnly: true,
        showActiveDynamicVolumeInVpd: false,
        showActiveDynamicVolumeOnly: false,
        showElementaryAirspaceOnly: true,
        showIfpzPartOfTrajectoryOnly: false,
    };

    public static contextType = NMUI4DContext;
    private nmui4dContext?: NMUI4DContextProps;

    constructor(props: RadRestrictionMapPanelProps) {
        super(props);
        this.state = {
            refTime: this.props.selectedTimePeriod.from,
            displayConvection: true,
            displayOffload: true,
            displayOnload: true,
            displayTv: true,
            displayAltRoutes: true,
            measureLayer: this.initMeasureLayer(),
        };
    }

    componentDidMount() {
        this.nmui4dContext = this.context as NMUI4DContextProps;

        this.updateRadRestrictionMapDisplay(this.props.selectedRadRestriction);
        this.initBusEventListeners();
        this.updateMeasureLayerDisplay(this.props.tvCache);
    }

    componentWillUnmount() {
        AppContext.eventBus.unregister(LayerUpdateEvent.NAME, this.layerUpdateEventCallBack);
        AppContext.eventBus.unregister(MapTrafficVolumeCacheUpdated.NAME, this.mapTrafficVolumeCacheUpdatedCallBack);
    }

    private layerUpdateEventCallBack = (event: LayerUpdateEvent) => {
        // This event is triggered by the LayerSelectionEntry (change of visibility or customProperties)
        // Update the layer and trigger rerender
        if (event.mapId === this.mapId && event.layer.id === this.state.measureLayer.id) {
            this.setState({ measureLayer: event.layer }, () => this.updateMeasureLayerDisplay(this.props.tvCache));
        }
    };

    private mapTrafficVolumeCacheUpdatedCallBack = (event: MapTrafficVolumeCacheUpdated) => {
        if (this.hasRelevantCacheUpdates(event.previousCache, event.cache)) {
            //only refresh map if the new cache value has relevant data for the layer
            this.updateMeasureLayerDisplay(event.cache);
        }
    };

    initBusEventListeners() {
        AppContext.eventBus.register(LayerUpdateEvent.NAME, this.layerUpdateEventCallBack);
        AppContext.eventBus.register(MapTrafficVolumeCacheUpdated.NAME, this.mapTrafficVolumeCacheUpdatedCallBack);
    }

    private hasRelevantCacheUpdates(prevCache: MapTrafficVolumeCacheType, cache: MapTrafficVolumeCacheType) {
        const layerTvIds = this.props.monitoredMeasures.map(mm => MeasureUtils.getTvLocation(mm.measure).id);
        const prevCacheTvIds = Object.keys(prevCache).filter(tvId => layerTvIds.includes(tvId));
        const cacheTvIds = Object.keys(cache).filter(tvId => layerTvIds.includes(tvId));
        return difference(cacheTvIds, prevCacheTvIds).length > 0;
    }

    private resilientAirspaceRetrieval(airspaceReferences: AirspaceRef[]) {
        ServiceFactory.coreService()
            .getAirspaceList({
                dataset: AppContext.getDataset(),
                airspaceReferences: airspaceReferences,
            } as AirspaceListRequest)
            .then(asReply => {
                if (asReply.error) {
                    throw new Error(asReply.error.message);
                } else {
                    this.setState({
                        airspaceData: asReply.data,
                    });
                }
            })
            .catch(reason => {
                if (reason.message.includes('Unknown airspace') && reason.message != null) {
                    const filteredAirspaceRefs = airspaceReferences.filter(
                        ref => !(reason.message as string).includes(ref.id),
                    );
                    if (filteredAirspaceRefs.length < airspaceReferences.length) {
                        this.resilientAirspaceRetrieval(filteredAirspaceRefs);
                    }
                }
            });
    }

    componentDidUpdate(
        prevProps: Readonly<RadRestrictionMapPanelProps>,
        prevState: Readonly<RadRestrictionMapPanelState>,
        snapshot?: any,
    ) {
        if (
            this.props.selectedRadRestriction !== prevProps.selectedRadRestriction ||
            this.state.displayOnload !== prevState.displayOnload ||
            this.state.displayOffload !== prevState.displayOffload
        ) {
            this.updateRadRestrictionMapDisplay(this.props.selectedRadRestriction);
        }
        if (this.props.selectedFlights !== prevProps.selectedFlights) {
            this.updateScenarioFlightsMapDisplay(this.props.selectedFlights);
        }
        if (this.props.selectedTimePeriod !== prevProps.selectedTimePeriod) {
            this.updateMeasureLayerDisplay(this.props.tvCache);
            this.setState({
                refTime: this.props.selectedTimePeriod.from,
            });
        }
    }

    private updateScenarioFlightsMapDisplay(flights: FlightDto[]) {
        ServiceFactory.sol49Service()
            .getMapFlightsData({
                clazz: SOL49_MAP_FLIGHTS_DATA_REQUEST_DTO_CLAZZ_VALUE,
                dataset: AppContext.getDataset(),
                flightProfileTrafficType: TrafficType.LOAD,
                flights: flights.map(flight => flight.flightId),
            } as Sol49MapFlightsDataRequestDto)
            .then(flightsMapReply => {
                if (flightsMapReply.error) {
                    throw new Error(flightsMapReply.error.message);
                } else {
                    this.setState({
                        mapFlightsData: flightsMapReply.data,
                    });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            });
    }

    private initMeasureLayer() {
        return {
            objectsToDisplay: MapLayerUtils.toObjectToDisplayArray(
                this.getActiveMeasures(this.props.monitoredMeasures),
                MapObjectType.MEASURE,
            ),
            selection: [],
            label: 'Measure',
            mapType: [MapObjectType.MEASURE],
            priority: 5.1,
            showLabel: false,
            active: false,
            type: MapLayerType.MEASURE,
            id: 2,
            customProperties: {
                [EnvironmentLayerUtils.REGULATED_SECTORS]: false,
                [EnvironmentLayerUtils.REGULATED_LAYER_MIN_DELAY]: RegulatedLayerDelayLevelEnum.ANY,
            },
        };
    }

    private getActiveMeasures = memoizeOne((monitoredMeasures: MonitoredMeasure[]) => {
        return monitoredMeasures.filter(({ measure }) => {
            return (
                MeasureUtils.isRegulation(measure) &&
                (measure.regulationState === RegulationState.APPLIED ||
                    measure.regulationState === RegulationState.APPLYING)
            );
        });
    });

    private getRegulationMapObjects = memoizeOne(
        (objectsToDisplay: ObjectToDisplay[], tvCache: MapTrafficVolumeCacheType) => {
            return objectsToDisplay.map((obj, index) => this.getRegulationMapObject(obj, index, tvCache));
        },
    );

    private updateMeasureLayerDisplay(tvCache: MapTrafficVolumeCacheType) {
        const objectsToDisplay = this.state.measureLayer.objectsToDisplay;
        const missingTvs = LayerSelectionEntry.retrievesRequiredDefinitions(tvCache, objectsToDisplay);
        if (!missingTvs) {
            ShowMapLayerEvent.fire(
                this.mapId,
                this.state.measureLayer,
                this.getRegulationMapObjects(objectsToDisplay, tvCache),
                this.props.selectedTimePeriod,
                { min: FlightLevelUtils.ON_GROUND_LEVEL, max: FlightLevelUtils.UNLIMITED_LEVEL },
            );
        }
    }

    private getRegulationMapObject = (obj: ObjectToDisplay, index: number, cache: MapTrafficVolumeCacheType) => {
        return {
            measure: obj.object,
            index: index,
            mapTv: cache[(obj.object['measure'] as RegulationOrMcdmOnlyDto).location.id],
            selected: false,
        } as MonitorableMapMeasure;
    };

    private updateRadRestrictionMapDisplay(radRestriction: RadRestriction) {
        this.setState({
            airspaceData: null,
            mapFlightsData: null,
        });
        const airspaceReferences: AirspaceRef[] = [];
        if (this.state.displayOnload && radRestriction.onloadedAreas) {
            airspaceReferences.push(
                ...radRestriction.onloadedAreas.map(airspaceId => {
                    return {
                        id: airspaceId,
                    } as AirspaceRef;
                }),
            );
        }
        if (this.state.displayOffload && radRestriction.offloadedAreas) {
            airspaceReferences.push(
                ...radRestriction.offloadedAreas.map(airspaceId => {
                    return {
                        id: airspaceId,
                    } as AirspaceRef;
                }),
            );
        }

        if (airspaceReferences.length > 0) {
            this.resilientAirspaceRetrieval(airspaceReferences);
        }
    }

    private handleMapRefTimeChange = (selectedRefTime: Date) => {
        this.setState({
            refTime: selectedRefTime,
        });
    };

    render() {
        // NOTE(bjarne): In mapless mode the UnityApplication is already created in App.tsx.
        return (
            <Flex direction="vertical" className="layout-rad-restriction-map-id applicable-rad-restrictions-sub-panel">
                {this.renderMenu()}
                {!this.nmui4dContext?.isMapless && <UnityApplication></UnityApplication>}
            </Flex>
        );
    }

    renderMenu() {
        const timePeriodInMinutes =
            (this.props.selectedTimePeriod.to.getTime() - this.props.selectedTimePeriod.from.getTime()) / (60 * 1000);
        let timeSelectorMark: number;
        if (timePeriodInMinutes > 240) {
            timeSelectorMark = 30;
        } else if (timePeriodInMinutes > 120) {
            timeSelectorMark = 20;
        } else if (timePeriodInMinutes > 60) {
            timeSelectorMark = 15;
        } else if (timePeriodInMinutes > 30) {
            timeSelectorMark = 10;
        } else {
            timeSelectorMark = 5;
        }
        return (
            <Flex size="9em" direction="vertical" className={'header'}>
                <Flex direction={'vertical'} style={{ gap: '1em', alignSelf: 'center' }}>
                    <Flex
                        direction={'horizontal'}
                        style={{ gap: '0.5em', alignItems: 'center' }}
                        className={'radRestriction-map-selection'}>
                        <Flex>Time Selection:</Flex>
                        <TimeSelector
                            width="13em"
                            timePeriod={this.props.selectedTimePeriod}
                            value={this.state.refTime}
                            onChange={this.handleMapRefTimeChange}
                            currentTickColor={'#60B1CC'}
                            markIntervalMs={timeSelectorMark * TimeUtils.MIN_TO_MS}
                        />
                        {this.renderMenuButton(this.state.displayConvection, 'Weather', () =>
                            this.setState(state => {
                                return { displayConvection: !state.displayConvection };
                            }),
                        )}
                        {this.state.measureLayer && (
                            <LayerSelectionEntryShortcut
                                minWidthEm={'10em'}
                                mapId={this.mapId}
                                layer={this.state.measureLayer}
                            />
                        )}
                        {this.renderMenuButton(this.state.displayOffload, 'Offload', () =>
                            this.setState(state => {
                                return { displayOffload: !state.displayOffload };
                            }),
                        )}
                        {this.renderMenuButton(this.state.displayOnload, 'OnLoad', () =>
                            this.setState(state => {
                                return { displayOnload: !state.displayOnload };
                            }),
                        )}
                    </Flex>
                </Flex>
            </Flex>
        );
    }

    renderMenuButton(isCurrentlyEnabled: boolean, label: string, handleClick: () => void) {
        let flexStyle: CSSProperties = {
            minWidth: '7em',
            maxWidth: '20em',
            height: '2em',
            textAlign: 'center',
            alignItems: 'stretch',
            justifyContent: 'center',
            gap: '1em', //space between label and settings button
            cursor: 'pointer',
            paddingLeft: 0,
            paddingRight: 0,
        };
        if (isCurrentlyEnabled) {
            flexStyle = Object.assign(flexStyle, {
                background: '#101010',
            });
        }
        return (
            <Flex
                direction={'vertical'}
                style={flexStyle}
                className={`ant-btn detailed-subpanel-title ${isCurrentlyEnabled ? 'active' : ''}`}>
                <span className="iconOnSpan" onClick={handleClick}>
                    {label}
                </span>
            </Flex>
        );
    }

    private airspaceStyleProvider = (airspace: Airspace): null | style.Style => {
        if (
            this.props.selectedRadRestriction.onloadedAreas &&
            this.props.selectedRadRestriction.onloadedAreas.find(as => as === airspace.reference.id)
        ) {
            return OLMapStyles.AIRSPACE_STYLE_SCENARIO_ONLOAD.clone();
        } else if (
            this.props.selectedRadRestriction.offloadedAreas &&
            this.props.selectedRadRestriction.offloadedAreas.find(as => as === airspace.reference.id)
        ) {
            return OLMapStyles.AIRSPACE_STYLE_SCENARIO_OFFLOAD.clone();
        } else {
            return OLMapStyles.TV_SELECTED_STYLE.clone();
        }
    };
}

export default connector(RadMapPanel);
